import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Trophy, Medal, Star, Target, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Prize } from '@/hooks/usePrizes';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PrizeFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  monthYear: string;
  existingPrize?: Prize | null;
  onSave: (data: {
    prizeName: string;
    prizeDescription: string;
    prizeType: string;
    prizeCategory: string;
    prizeValue: number | null;
  }) => Promise<{ success: boolean }>;
  formatMonthYear: (monthYear: string) => string;
}

const PRIZE_TYPES = [
  { value: 'ranking_winner', label: 'Ganador Ranking', icon: Trophy, color: 'text-amber-400' },
  { value: 'achievement_badge', label: 'Por Logro', icon: Medal, color: 'text-purple-400' },
  { value: 'special_event', label: 'Evento Especial', icon: Star, color: 'text-pink-400' },
  { value: 'milestone', label: 'Meta Cumplida', icon: Target, color: 'text-blue-400' },
];

const ACHIEVEMENT_OPTIONS = [
  { value: 'training_complete', label: 'Entrenamiento completo' },
  { value: 'first_sale', label: 'Primera venta' },
  { value: 'sales_50', label: '50 ventas' },
  { value: 'sales_100', label: '100 ventas' },
  { value: 'perfect_month', label: 'Mes perfecto' },
];

export function PrizeFormModal({
  open,
  onOpenChange,
  monthYear,
  existingPrize,
  onSave,
  formatMonthYear,
}: PrizeFormModalProps) {
  const [prizeName, setPrizeName] = useState('');
  const [prizeDescription, setPrizeDescription] = useState('');
  const [prizeType, setPrizeType] = useState('ranking_winner');
  const [prizeCategory, setPrizeCategory] = useState('');
  const [prizeValue, setPrizeValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existingPrize) {
      setPrizeName(existingPrize.prize_name);
      setPrizeDescription(existingPrize.prize_description || '');
      setPrizeType((existingPrize as any).prize_type || 'ranking_winner');
      setPrizeCategory((existingPrize as any).prize_category || '');
      setPrizeValue((existingPrize as any).prize_value?.toString() || '');
    } else {
      setPrizeName('');
      setPrizeDescription('');
      setPrizeType('ranking_winner');
      setPrizeCategory('');
      setPrizeValue('');
    }
  }, [existingPrize, open]);

  const handleSubmit = async () => {
    if (!prizeName.trim()) return;

    setSaving(true);
    const result = await onSave({
      prizeName,
      prizeDescription,
      prizeType,
      prizeCategory,
      prizeValue: prizeValue ? parseInt(prizeValue) : null,
    });
    setSaving(false);

    if (result.success) {
      onOpenChange(false);
    }
  };

  const selectedType = PRIZE_TYPES.find(t => t.value === prizeType);
  const TypeIcon = selectedType?.icon || Gift;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <div className={`p-2 rounded-lg bg-amber-500/20`}>
              <TypeIcon className={`h-5 w-5 ${selectedType?.color || 'text-amber-400'}`} />
            </div>
            {existingPrize ? 'Editar Premio' : 'Nuevo Premio'}
            <span className="text-sm font-normal text-zinc-400 ml-2">
              {formatMonthYear(monthYear)}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Prize Type */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Tipo de Premio</Label>
            <div className="grid grid-cols-2 gap-2">
              {PRIZE_TYPES.map((type) => (
                <motion.button
                  key={type.value}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setPrizeType(type.value)}
                  className={`
                    flex items-center gap-2 p-3 rounded-lg border transition-all
                    ${prizeType === type.value
                      ? 'bg-amber-500/20 border-amber-500'
                      : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }
                  `}
                >
                  <type.icon className={`h-4 w-4 ${type.color}`} />
                  <span className={`text-sm ${prizeType === type.value ? 'text-white' : 'text-zinc-400'}`}>
                    {type.label}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Achievement Category (only for achievement_badge) */}
          {prizeType === 'achievement_badge' && (
            <div className="space-y-2">
              <Label className="text-zinc-400">Logro Requerido</Label>
              <Select value={prizeCategory} onValueChange={setPrizeCategory}>
                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white">
                  <SelectValue placeholder="Selecciona el logro" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {ACHIEVEMENT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} className="text-white">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Prize Name */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Nombre del Premio</Label>
            <Input
              placeholder="Ej: Día libre + ₡20,000"
              value={prizeName}
              onChange={(e) => setPrizeName(e.target.value)}
              className="bg-zinc-800/50 border-zinc-700 text-white"
            />
          </div>

          {/* Prize Description */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Descripción (opcional)</Label>
            <Textarea
              placeholder="Describe el premio..."
              value={prizeDescription}
              onChange={(e) => setPrizeDescription(e.target.value)}
              className="bg-zinc-800/50 border-zinc-700 text-white"
              rows={2}
            />
          </div>

          {/* Prize Value */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Valor Estimado (₡)</Label>
            <Input
              type="number"
              placeholder="20000"
              value={prizeValue}
              onChange={(e) => setPrizeValue(e.target.value)}
              className="bg-zinc-800/50 border-zinc-700 text-white"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || !prizeName.trim()}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}